import type { FC } from "react"

import styles from "./FeatureFlagTab.module.scss"

import BulkToggleRow from "./bulk-toggle-row/BulkToggleRow"
import CompanyWideRow from "./company-wide-row/CompanyWideRow"
import SalonRow from "./salon-row/SalonRow"
import { useFeatureFlagTab } from "./useFeatureFlagTab"

import SaveIcon from "@/lib/images/SaveIcon"
import { getFragmentData } from "@/souhl_admin/gql/__generated__"
import { graphql } from "@/souhl_admin/gql/__generated__/gql"

// ヘッダー列 (フラグ名) 描画用。行 fragment を並列 spread して子コンポーネントに伝搬させる。
const FeatureFlagTabFragment = graphql(/* GraphQL */ `
	fragment FeatureFlagTab on CompanyFeatureFlag {
		featureFlag {
			id
			name
			# フラグ名のマウスオーバーでツールチップ表示する説明文
			comment
		}
		...CompanyWideRowCompanyFeatureFlag
		...BulkToggleRowCompanyFeatureFlag
		...SalonRowCompanyFeatureFlag
	}
`)

const FeatureFlagTabSalonFragment = graphql(/* GraphQL */ `
	fragment FeatureFlagTabSalon on Salon {
		id
		...BulkToggleRowSalon
		...SalonRowSalon
	}
`)

type FeatureFlagTabProps = {
	companyId: string
}

const getSaveButtonLabel = (saving: boolean): string => {
	if (saving) return "保存中..."
	return "保存"
}

const FeatureFlagTab: FC<FeatureFlagTabProps> = ({ companyId }) => {
	const {
		companyFeatureFlags,
		salons,
		flagStates,
		isDirty,
		saving,
		isInitialLoading,
		openDropdownFlagId,
		handleToggleDropdown,
		handleCompanyWideToggle,
		handleSalonToggle,
		handleBulkToggle,
		handleSave,
	} = useFeatureFlagTab(companyId)

	// FeatureFlagTab fragment の unmask 結果には子 fragment の ref が含まれる。
	// 子コンポーネントはこれをそのまま受け取り、自分の fragment を個別 unmask する。
	const tabItems = companyFeatureFlags.map((cf) =>
		getFragmentData(FeatureFlagTabFragment, cf)
	)
	const tabSalons = salons.map((s) =>
		getFragmentData(FeatureFlagTabSalonFragment, s)
	)

	if (isInitialLoading) {
		return (
			<div className={styles.loadingContainer}>
				<div className={styles.loading}>読み込み中...</div>
			</div>
		)
	}

	if (tabItems.length === 0) {
		return (
			<div className={styles.featureFlagTab}>
				<div className={styles.emptyMessage}>
					Feature Flag が定義されていません
				</div>
			</div>
		)
	}

	return (
		<div className={styles.featureFlagTab}>
			<div className={styles.card}>
				<div className={styles.header}>
					<h2 className={styles.title}>Feature Flag 設定</h2>
					<button
						className={styles.saveButton}
						onClick={handleSave}
						disabled={!isDirty || saving}
					>
						<SaveIcon />
						{getSaveButtonLabel(saving)}
					</button>
				</div>

				<div className={styles.tableWrapper}>
					<table className={styles.table}>
						<thead>
							<tr>
								<th className={styles.labelTh} />
								{tabItems.map((cf) => (
									<th key={cf.featureFlag.id} className={styles.flagTh}>
										<span className={styles.flagNameWrapper}>
											{cf.featureFlag.name}
											{cf.featureFlag.comment && (
												<span className={styles.flagTooltip} role="tooltip">
													{cf.featureFlag.comment}
												</span>
											)}
										</span>
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							<CompanyWideRow
								companyFeatureFlags={tabItems}
								flagStates={flagStates}
								openDropdownFlagId={openDropdownFlagId}
								onToggleDropdown={handleToggleDropdown}
								onSelect={handleCompanyWideToggle}
							/>
							<BulkToggleRow
								companyFeatureFlags={tabItems}
								salons={tabSalons}
								flagStates={flagStates}
								onBulkToggle={handleBulkToggle}
							/>
							{tabSalons.map((salon, index) => (
								<SalonRow
									key={salon.id}
									salon={salon}
									isLast={index === tabSalons.length - 1}
									companyFeatureFlags={tabItems}
									flagStates={flagStates}
									onSalonToggle={handleSalonToggle}
								/>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

export default FeatureFlagTab
